import { useMutation } from '@tanstack/react-query';
import {
  createMassiveArticles,
  createMassiveFormats,
  createMassiveProviderFormats,
  createMassiveProviderFormatsT2,
} from '../../../../api/carteleriaApi';
import toast from 'react-hot-toast';
import { useAuth0 } from '@auth0/auth0-react';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

const chunkArray = (array, size) => {
  const chunks = [];

  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size));
  }

  return chunks;
};

const useCreateMassiveArticles = () => {
  const queryClient = useQueryClient();
  const { getAccessTokenSilently } = useAuth0();
  const [progress, setProgress] = useState({ message: '', pct: 0 });

  const { data, mutateAsync, isLoading, error } = useMutation({
    mutationFn: async (body) => {
      const token = await getAccessTokenSilently();

      // Combinar new + edited para cada entidad (withOutChanges no necesita subirse)
      const articles = [...(body.newArticles || []), ...(body.editedArticles || [])];
      const formats = [...(body.newFormats || []), ...(body.editedFormats || [])];
      const providersFormats = [...(body.newProvidersFormats || []), ...(body.editedProvidersFormats || [])];
      const deletedProviderFormatIds = body.deletedProviderFormatIds || [];
      const isTemplate2 = body.templateType === 'template-2';

      const totalArticles = articles.length;
      const totalFormats = formats.length;
      const totalProvidersFormats = providersFormats.length;

      const batchSize = 100;
      const articleBatches = chunkArray(articles, batchSize);
      const formatBatches = chunkArray(formats, batchSize);
      const providerFormatBatches = chunkArray(providersFormats, batchSize);

      const totalBatches = articleBatches.length + formatBatches.length + providerFormatBatches.length;
      let completedBatches = 0;

      let processedArticles = 0;
      let processedFormats = 0;
      let processedProvidersFormats = 0;

      // Mapas acumulados entre fases para resolver códigos temporales → _id real
      const articleCodeMap = {};
      const formatCodeMap = {};

      setProgress({ message: `Fase 1/3: subiendo artículos 0/${totalArticles}...`, pct: 0 });

      try {
        // ── Fase 1: Artículos ─────────────────────────────────────────────────
        for (let i = 0; i < articleBatches.length; i++) {
          const result = await createMassiveArticles(token, { articles: articleBatches[i] });

          Object.assign(articleCodeMap, result.data?.articleCodeMap || {});
          processedArticles += articleBatches[i].length;
          completedBatches++;

          setProgress({
            message: `Fase 1/3: subiendo artículos ${processedArticles}/${totalArticles}...`,
            pct: Math.round((completedBatches / totalBatches) * 100),
          });
        }

        // ── Fase 2: Formatos ──────────────────────────────────────────────────
        for (let i = 0; i < formatBatches.length; i++) {
          setProgress({
            message: `Fase 2/3: subiendo formatos ${processedFormats}/${totalFormats}...`,
            pct: Math.round((completedBatches / totalBatches) * 100),
          });

          const result = await createMassiveFormats(token, {
            formats: formatBatches[i],
            articleCodeMap,
          });

          Object.assign(formatCodeMap, result.data?.formatCodeMap || {});
          processedFormats += formatBatches[i].length;
          completedBatches++;

          setProgress({
            message: `Fase 2/3: subiendo formatos ${processedFormats}/${totalFormats}...`,
            pct: Math.round((completedBatches / totalBatches) * 100),
          });
        }

        // ── Fase 3: Proveedores ───────────────────────────────────────────────
        for (let i = 0; i < providerFormatBatches.length; i++) {
          setProgress({
            message: `Fase 3/3: subiendo proveedores ${processedProvidersFormats}/${totalProvidersFormats}...`,
            pct: Math.round((completedBatches / totalBatches) * 100),
          });

          const isLastBatch = i === providerFormatBatches.length - 1;
          const payload = {
            providersFormats: providerFormatBatches[i],
            articleCodeMap,
            formatCodeMap,
            ...(isTemplate2 && isLastBatch ? { deletedProviderFormatIds } : {}),
          };

          if (isTemplate2) {
            await createMassiveProviderFormatsT2(token, payload);
          } else {
            await createMassiveProviderFormats(token, payload);
          }

          processedProvidersFormats += providerFormatBatches[i].length;
          completedBatches++;

          setProgress({
            message: `Fase 3/3: subiendo proveedores ${processedProvidersFormats}/${totalProvidersFormats}...`,
            pct: Math.round((completedBatches / totalBatches) * 100),
          });
        }

        // Si hay deletes pero no había PFs new/edited, dispararlos igualmente
        if (isTemplate2 && providerFormatBatches.length === 0 && deletedProviderFormatIds.length > 0) {
          await createMassiveProviderFormatsT2(token, {
            providersFormats: [],
            articleCodeMap,
            formatCodeMap,
            deletedProviderFormatIds,
          });
        }

        setProgress({ message: 'Completado', pct: 100 });
        toast.success(
          `Subida completada. Artículos: ${processedArticles}/${totalArticles}. Formatos: ${processedFormats}/${totalFormats}. Proveedores: ${processedProvidersFormats}/${totalProvidersFormats}.`,
        );

        return { articleCodeMap, formatCodeMap };
      } catch (error) {
        toast.error(
          `Error en la subida. Artículos: ${processedArticles}/${totalArticles}. Formatos: ${processedFormats}/${totalFormats}. Proveedores: ${processedProvidersFormats}/${totalProvidersFormats}.`,
        );
        throw error;
      }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries('all-artes-finales-master');
      return data;
    },
    onError: (error) => {
      return error;
    },
  });

  return {
    data,
    mutateAsync,
    isLoading,
    error,
    progress,
  };
};

export default useCreateMassiveArticles;
